import type { PartnerFieldConfigFragmentFragment } from '@/gql/graphql';

/**
 * One entry of `partner.fieldConfig`: a partner-caller field the dashboards display, with its
 * label and placeholder. Inputs use `required` directly; the `partnerEmergencyContact` section's
 * `required` applies to all of its sub-fields, whose own entries only override labels/placeholders.
 * Validation rules (digit counts etc.) stay in code, see `usePartner`.
 */
export type PartnerFieldConfigEntry = PartnerFieldConfigFragmentFragment;

/** `null`/`undefined` means the partner has no config (callers use their fallback); `[]` means "no partner-caller fields". */
export type PartnerFieldConfig = PartnerFieldConfigEntry[] | null | undefined;

export type PartnerWithFieldConfig = { fieldConfig?: PartnerFieldConfig } | null | undefined;

type LabelledItem = { key: string; label: string } | { field: string; label: string };

/** What registration shows for a partner without a config: only the optional external caller id, as before the config existed. */
export const DEFAULT_REGISTRATION_FIELD_CONFIG: PartnerFieldConfigEntry[] = [
  { field: 'externalPartnerCallerId', label: 'External Caller ID', placeholder: 'External Caller ID', required: false },
];

export const getPartnerFieldConfig = (partner: PartnerWithFieldConfig, fallback: PartnerFieldConfigEntry[] = []): PartnerFieldConfigEntry[] =>
  partner?.fieldConfig ?? fallback;

export const findPartnerField = (fieldConfig: PartnerFieldConfig, field: string): PartnerFieldConfigEntry | null =>
  fieldConfig?.find((entry) => entry.field === field) ?? null;

/**
 * Replaces the label of every item whose key/field is configured for the partner.
 * Example, with `[{ field: 'externalPartnerCallerId', label: 'UCI #' }]`:
 *   [{ key: 'externalPartnerCallerId', label: 'External Caller ID' }, { key: 'name', label: 'Name' }]
 *   -> [{ key: 'externalPartnerCallerId', label: 'UCI #' }, { key: 'name', label: 'Name' }]
 */
export const applyPartnerFieldLabels = <T extends LabelledItem>(fieldConfig: PartnerFieldConfig, items: T[]): T[] =>
  items.map((item) => {
    const field = 'field' in item ? item.field : item.key;
    const label = findPartnerField(fieldConfig, field)?.label;

    return label ? { ...item, label } : item;
  });
